import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Layer, LayoutElement, TrackNodeAnnotation, TransformControls } from '../../types';
import { generateId } from '../../utils/geometry';
import { createEntityIdAllocator, formatPortId } from '../../utils/entityIds';
import {
  makePort,
  nextPortIndex,
  defaultPortKindToAdd,
  defaultPortDisplayName,
  addPortToList,
  updatePortInList,
  removePortFromList,
  alignStockerPortsToTrayEdge,
  normalizePorts,
  type Port,
  type PortParentType,
} from '../../utils/ports';
import { isDxfEquipmentLayoutElement } from '../../utils/layout3d';
import {
  findTrackNodeAnnotation,
  isMeaningfulTrackNodeAnnotation,
} from '../../utils/trackNodeAnnotations';
import { computeNextEntityName } from '../../utils/entityNaming';
import { findOHTLaneSnapPosition } from '../../utils/topology';

/**
 * Whether a layout element's ports behave like tool docks or stocker racks.
 * DXF-imported equipment is the layout-layer form of a tool; everything else
 * that carries ports (stockers, STB/OHB/lifter/area seeds) uses stocker kinds.
 */
export function portParentTypeForElement(element: LayoutElement): PortParentType {
  return isDxfEquipmentLayoutElement(element) ? 'equipment' : 'stocker';
}

/** Read the ports stored on a layout element, defaulting to []. */
export function getElementPorts(element: LayoutElement | undefined): Port[] {
  const ports = element?.customProperties?.ports;
  if (!Array.isArray(ports)) return [];
  if (!element) return ports as Port[];
  return normalizePorts(element.id, portParentTypeForElement(element), ports as Port[]);
}

/** Write a ports array back onto a layout element's customProperties. */
function setElementPorts(element: LayoutElement, ports: Port[]) {
  element.customProperties = { ...(element.customProperties || {}), ports };
}

export const DEFAULT_LAYER_ID = 'layer-1';
export const DEFAULT_LAYER_NAME = 'Layer 1';

/** Keep OHT vehicles at the end of elementOrder so they always paint on top. */
export function withOhtsOnTop(order: string[], elements: Record<string, LayoutElement>): string[] {
  const nonOht: string[] = [];
  const oht: string[] = [];
  for (const id of order) {
    if (elements[id]?.type === 'oht-vehicle') oht.push(id);
    else nonOht.push(id);
  }
  return [...nonOht, ...oht];
}

interface LayoutState {
  elements: Record<string, LayoutElement>;
  elementOrder: string[]; // For rendering order
  layers: Record<string, Layer>;
  layerOrder: string[];
  activeLayerId: string;
  /** Stocker element whose ports are currently being dragged/edited on canvas. */
  portEditElementId: string | null;
  /** Port selected for Properties panel inspection (null = none). */
  selectedPortId: string | null;
  /** Simulation track node id (`NODE-N`) selected for the Properties panel. */
  selectedTrackNodeId: string | null;
  /** Persisted entry/exit names for track nodes (matched by world position). */
  trackNodeAnnotations: TrackNodeAnnotation[];
  /** Project-level metadata from loaded layout JSON (inventory / transportFlow / …). */
  projectMetadata: Record<string, unknown> | null;
  /** Tracks whether the user has unsaved modifications. */
  isDirty: boolean;
}

/** Types that support connection ports on the layout layer. */
const PORT_SUPPORTING_TYPES: ReadonlySet<LayoutElement['type']> = new Set([
  'stocker',
  'stb',
  'ohb',
  'lifter',
  'rectangle',
]);

/** Seed the default docks onto a port-supporting element that has none yet. */
const ensureElementHasPort = (element: LayoutElement) => {
  if (!PORT_SUPPORTING_TYPES.has(element.type)) return;
  const stored = element.customProperties?.ports;
  const parentType = portParentTypeForElement(element);
  const ports = normalizePorts(
    element.id,
    parentType,
    Array.isArray(stored) ? (stored as Port[]) : []
  );
  // Tools keep their bottom (track-facing) docks; only stockers migrate the
  // legacy pair onto the tray edge.
  const aligned = parentType === 'stocker' ? alignStockerPortsToTrayEdge(ports) : ports;
  setElementPorts(element, aligned);
};

/** Deep-clone a duplicated element's ports so edits don't mutate the source. */
const cloneElementPortsForDuplicate = (source: LayoutElement, duplicate: LayoutElement) => {
  const ports = getElementPorts(source);
  if (ports.length === 0) return;
  setElementPorts(
    duplicate,
    ports.map((p, index) => ({
      ...p,
      id: formatPortId(duplicate.id, index + 1),
      parentId: duplicate.id,
    }))
  );
};

const createDefaultLayer = (): Layer => ({
  id: DEFAULT_LAYER_ID,
  name: DEFAULT_LAYER_NAME,
  visible: true,
  locked: false,
  zIndex: 0,
  elementIds: [],
});

const initialState: LayoutState = {
  elements: {},
  elementOrder: [],
  layers: {
    [DEFAULT_LAYER_ID]: createDefaultLayer(),
  },
  layerOrder: [DEFAULT_LAYER_ID],
  activeLayerId: DEFAULT_LAYER_ID,
  portEditElementId: null,
  selectedPortId: null,
  selectedTrackNodeId: null,
  trackNodeAnnotations: [],
  projectMetadata: null,
  isDirty: false,
};

function upsertTrackNodeAnnotation(
  annotations: TrackNodeAnnotation[],
  x: number,
  y: number,
  changes: Partial<Pick<TrackNodeAnnotation, 'name' | 'role' | 'foupCapacity' | 'isJunction'>>,
  defaultName: string
): TrackNodeAnnotation[] {
  const existing = findTrackNodeAnnotation(annotations, x, y);
  const next: TrackNodeAnnotation = {
    x: existing?.x ?? x,
    y: existing?.y ?? y,
    name: changes.name !== undefined ? changes.name : (existing?.name ?? defaultName),
    role: changes.role !== undefined ? changes.role : (existing?.role ?? 'none'),
  };
  const nextCapacity =
    changes.foupCapacity !== undefined ? changes.foupCapacity : existing?.foupCapacity;
  if (typeof nextCapacity === 'number' && Number.isFinite(nextCapacity) && nextCapacity > 0) {
    next.foupCapacity = nextCapacity;
  }
  const nextIsJunction =
    changes.isJunction !== undefined ? changes.isJunction : existing?.isJunction;
  if (nextIsJunction) next.isJunction = true;

  const clearedRole = changes.role === 'entry' || changes.role === 'exit' ? changes.role : null;

  const updated = annotations
    .filter((annotation) => annotation !== existing)
    .map((annotation) =>
      clearedRole && annotation.role === clearedRole
        ? { ...annotation, role: 'none' as const }
        : annotation
    )
    .filter(isMeaningfulTrackNodeAnnotation);

  if (isMeaningfulTrackNodeAnnotation(next)) {
    updated.push(next);
  }

  return updated;
}

const ensureDefaultLayer = (state: LayoutState) => {
  if (!state.layers[DEFAULT_LAYER_ID]) {
    state.layers[DEFAULT_LAYER_ID] = createDefaultLayer();
  }
  if (!state.layerOrder.includes(DEFAULT_LAYER_ID)) {
    state.layerOrder.unshift(DEFAULT_LAYER_ID);
  }
};

const removeElementFromAllLayers = (state: LayoutState, elementId: string) => {
  Object.values(state.layers).forEach((layer) => {
    layer.elementIds = layer.elementIds.filter((id) => id !== elementId);
  });
};

const getValidTargetLayerId = (state: LayoutState, candidate?: string) => {
  ensureDefaultLayer(state);
  if (candidate && state.layers[candidate]) {
    return candidate;
  }
  return state.layers[state.activeLayerId] ? state.activeLayerId : DEFAULT_LAYER_ID;
};

const nextLayerName = (state: LayoutState) => {
  const existingNames = new Set(Object.values(state.layers).map((layer) => layer.name));
  let index = 2;
  let name = `Layer ${index}`;
  while (existingNames.has(name)) {
    index += 1;
    name = `Layer ${index}`;
  }
  return name;
};

const createAutoLayerForNewElements = (state: LayoutState) => {
  const id = generateId();
  const zIndex = state.layerOrder.length;
  state.layers[id] = {
    id,
    name: nextLayerName(state),
    visible: true,
    locked: false,
    zIndex,
    elementIds: [],
  };
  state.layerOrder.push(id);
  state.activeLayerId = id;
  return id;
};

const getNextEntityName = (
  state: LayoutState,
  type: LayoutElement['type'],
  additionalElements: LayoutElement[] = []
) => computeNextEntityName(type, [...Object.values(state.elements), ...additionalElements]);

const layoutSlice = createSlice({
  name: 'layout',
  initialState,
  reducers: {
    /**
     * Add a single layout element to the store.
     * If the target layer is locked, creates a new auto layer.
     *
     * The element is stored at the pose the caller resolved. Snapping belongs to
     * the drop/drag path, which previews the same result the operator commits;
     * re-snapping here would move the element away from where it was released.
     */
    addElement: (state, action: PayloadAction<LayoutElement>) => {
      let layerId = getValidTargetLayerId(state, action.payload.layerId);
      if (state.layers[layerId]?.locked) {
        layerId = createAutoLayerForNewElements(state);
      }
      const element: LayoutElement = { ...action.payload, layerId };
      ensureElementHasPort(element);
      state.elements[element.id] = element;
      state.elementOrder.push(element.id);
      state.elementOrder = withOhtsOnTop(state.elementOrder, state.elements);
      if (!state.layers[layerId].elementIds.includes(element.id)) {
        state.layers[layerId].elementIds.push(element.id);
      }
    },

    /**
     * Add multiple layout elements to the store.
     * Each element is validated and added to its target layer.
     */
    addElements: (state, action: PayloadAction<LayoutElement[]>) => {
      action.payload.forEach((element) => {
        const layerId = getValidTargetLayerId(state, element.layerId);
        const nextElement: LayoutElement = { ...element, layerId };
        ensureElementHasPort(nextElement);
        state.elements[nextElement.id] = nextElement;
        state.elementOrder.push(element.id);
        if (!state.layers[layerId].elementIds.includes(nextElement.id)) {
          state.layers[layerId].elementIds.push(nextElement.id);
        }
      });
      state.elementOrder = withOhtsOnTop(state.elementOrder, state.elements);
    },

    /**
     * Remove a single layout element by ID.
     * Also removes it from all layers.
     */
    removeElement: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      delete state.elements[id];
      state.elementOrder = state.elementOrder.filter((eId) => eId !== id);
      removeElementFromAllLayers(state, id);
      if (state.portEditElementId === id) state.portEditElementId = null;
      // Drop port selection if its parent was removed (or any selection — safe).
      if (state.selectedPortId) {
        const stillOwns = Object.values(state.elements).some((el) =>
          getElementPorts(el).some((p) => p.id === state.selectedPortId)
        );
        if (!stillOwns) state.selectedPortId = null;
      }
    },

    /**
     * Remove multiple layout elements by ID.
     * Removes them from all layers and maintains render order.
     */
    removeElements: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        delete state.elements[id];
        removeElementFromAllLayers(state, id);
      });
      state.elementOrder = state.elementOrder.filter((eId) => !action.payload.includes(eId));
      if (state.portEditElementId && action.payload.includes(state.portEditElementId)) {
        state.portEditElementId = null;
      }
      if (state.selectedPortId) {
        const stillOwns = Object.values(state.elements).some((el) =>
          getElementPorts(el).some((p) => p.id === state.selectedPortId)
        );
        if (!stillOwns) state.selectedPortId = null;
      }
    },

    /**
     * Update a single element's properties.
     * Handles layer transitions and validates target layer.
     */
    updateElement: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<LayoutElement> }>
    ) => {
      const { id, updates } = action.payload;
      if (state.elements[id]) {
        const currentElement = state.elements[id];
        const requestedLayerId = updates.layerId;
        const nextLayerId = requestedLayerId
          ? getValidTargetLayerId(state, requestedLayerId)
          : currentElement.layerId;

        if (nextLayerId !== currentElement.layerId) {
          removeElementFromAllLayers(state, id);
          state.layers[nextLayerId].elementIds.push(id);
        }

        state.elements[id] = {
          ...currentElement,
          ...updates,
          layerId: nextLayerId,
          ...(updates.customProperties
            ? {
                customProperties: {
                  ...(currentElement.customProperties || {}),
                  ...updates.customProperties,
                },
              }
            : {}),
          ...(updates.metadata
            ? {
                metadata: {
                  ...currentElement.metadata,
                  ...updates.metadata,
                },
              }
            : {}),
          ...(updates.dimensions
            ? {
                dimensions: {
                  ...currentElement.dimensions,
                  ...updates.dimensions,
                },
              }
            : {}),
          ...(updates.position
            ? {
                position: {
                  ...currentElement.position,
                  ...updates.position,
                },
              }
            : {}),
        };
      }
    },

    /**
     * Update multiple elements' properties.
     * Processes all updates in a batch.
     */
    updateElements: (
      state,
      action: PayloadAction<Array<{ id: string; updates: Partial<LayoutElement> }>>
    ) => {
      action.payload.forEach(({ id, updates }) => {
        if (state.elements[id]) {
          const currentElement = state.elements[id];
          const requestedLayerId = updates.layerId;
          const nextLayerId = requestedLayerId
            ? getValidTargetLayerId(state, requestedLayerId)
            : currentElement.layerId;

          if (nextLayerId !== currentElement.layerId) {
            removeElementFromAllLayers(state, id);
            state.layers[nextLayerId].elementIds.push(id);
          }

          state.elements[id] = {
            ...currentElement,
            ...updates,
            layerId: nextLayerId,
            ...(updates.customProperties
              ? {
                  customProperties: {
                    ...(currentElement.customProperties || {}),
                    ...updates.customProperties,
                  },
                }
              : {}),
            ...(updates.metadata
              ? {
                  metadata: {
                    ...currentElement.metadata,
                    ...updates.metadata,
                  },
                }
              : {}),
            ...(updates.dimensions
              ? {
                  dimensions: {
                    ...currentElement.dimensions,
                    ...updates.dimensions,
                  },
                }
              : {}),
            ...(updates.position
              ? {
                  position: {
                    ...currentElement.position,
                    ...updates.position,
                  },
                }
              : {}),
          };
        }
      });
    },

    transformElement: (
      state,
      action: PayloadAction<{ id: string; transform: TransformControls }>
    ) => {
      const { id, transform } = action.payload;
      const element = state.elements[id];
      if (!element) return;

      if (transform.position) {
        element.position = { ...element.position, ...transform.position };
      }
      if (transform.rotation !== undefined) {
        element.rotation = transform.rotation;
      }
      if (transform.dimensions) {
        element.dimensions = { ...element.dimensions, ...transform.dimensions };
      }
    },

    transformElements: (
      state,
      action: PayloadAction<Array<{ id: string; transform: TransformControls }>>
    ) => {
      action.payload.forEach(({ id, transform }) => {
        const element = state.elements[id];
        if (!element) return;

        if (transform.position) {
          element.position = { ...element.position, ...transform.position };
        }
        if (transform.rotation !== undefined) {
          element.rotation = transform.rotation;
        }
        if (transform.dimensions) {
          element.dimensions = { ...element.dimensions, ...transform.dimensions };
        }
      });
    },

    // ── Ports (layout stockers) ───────────────────────────────────────────
    setStockerPortEdit: (state, action: PayloadAction<string | null>) => {
      state.portEditElementId = action.payload;
    },
    /** Select a load port for the Properties panel (clears on null). */
    selectLayoutPort: (
      state,
      action: PayloadAction<{ elementId: string; portId: string } | null>
    ) => {
      if (!action.payload) {
        state.selectedPortId = null;
        return;
      }
      state.selectedPortId = action.payload.portId;
      state.selectedTrackNodeId = null;
    },
    /** Select a simulation track node for the Properties panel (clears on null). */
    selectTrackNode: (state, action: PayloadAction<string | null>) => {
      state.selectedTrackNodeId = action.payload;
      if (action.payload) {
        state.selectedPortId = null;
        state.portEditElementId = null;
      }
    },
    /** Upsert name/role for a track node; entry and exit roles remain globally unique. */
    updateTrackNodeAnnotation: (
      state,
      action: PayloadAction<{
        x: number;
        y: number;
        defaultName: string;
        changes: Partial<Pick<TrackNodeAnnotation, 'name' | 'role' | 'foupCapacity'>>;
      }>
    ) => {
      const { x, y, defaultName, changes } = action.payload;
      state.trackNodeAnnotations = upsertTrackNodeAnnotation(
        state.trackNodeAnnotations,
        x,
        y,
        changes,
        defaultName
      );
    },
    /**
     * Re-key the annotation at `from` to `to`. Annotations are matched to live
     * nodes by position, so a dragged node would otherwise lose its name, role
     * and gate capacity.
     */
    moveTrackNodeAnnotation: (
      state,
      action: PayloadAction<{
        from: { x: number; y: number };
        to: { x: number; y: number };
        tolerance?: number;
      }>
    ) => {
      const { from, to, tolerance = 2 } = action.payload;
      state.trackNodeAnnotations = state.trackNodeAnnotations.map((annotation) =>
        Math.hypot(annotation.x - from.x, annotation.y - from.y) <= tolerance
          ? { ...annotation, x: to.x, y: to.y }
          : annotation
      );
    },
    /** Drop annotation(s) near a world point and clear track-node selection. */
    clearTrackNodeAnnotationAt: (
      state,
      action: PayloadAction<{ x: number; y: number; tolerance?: number }>
    ) => {
      const { x, y, tolerance = 2 } = action.payload;
      state.trackNodeAnnotations = state.trackNodeAnnotations.filter(
        (annotation) => Math.hypot(annotation.x - x, annotation.y - y) > tolerance
      );
      state.selectedTrackNodeId = null;
    },
    addStockerPort: (state, action: PayloadAction<{ elementId: string; port?: Partial<Port> }>) => {
      const element = state.elements[action.payload.elementId];
      if (!element) return;
      const current = getElementPorts(element);
      const parentType = portParentTypeForElement(element);
      const portKind = defaultPortKindToAdd(parentType, current);
      const toolType = String(element.customProperties?.toolType ?? '').trim();
      const slot = nextPortIndex(current);
      const port = makePort({
        parentId: element.id,
        parentType,
        name: action.payload.port?.name ?? `LP-${slot}`,
        displayName:
          action.payload.port?.displayName ??
          defaultPortDisplayName({
            parentName: element.metadata.name,
            toolType,
            portKind: action.payload.port?.portKind ?? portKind,
            fallbackName: `LP-${slot}`,
          }),
        rx: 1,
        ry: 0.5,
        edge: 'right',
        portKind,
        portIndex: slot,
        ...action.payload.port,
      });
      setElementPorts(element, addPortToList(current, port));
    },
    updateStockerPort: (
      state,
      action: PayloadAction<{ elementId: string; portId: string; changes: Partial<Port> }>
    ) => {
      const element = state.elements[action.payload.elementId];
      if (!element) return;
      const current = getElementPorts(element);
      setElementPorts(
        element,
        updatePortInList(current, action.payload.portId, action.payload.changes)
      );
    },
    removeStockerPort: (state, action: PayloadAction<{ elementId: string; portId: string }>) => {
      const element = state.elements[action.payload.elementId];
      if (!element) return;
      const current = getElementPorts(element);
      // removePortFromList refuses to drop the final remaining port.
      setElementPorts(element, removePortFromList(current, action.payload.portId));
      if (state.selectedPortId === action.payload.portId) state.selectedPortId = null;
    },

    duplicateElement: (state, action: PayloadAction<string>) => {
      const original = state.elements[action.payload];
      if (!original) return;

      const sourceLayerId = original.layerId || original.metadata.layerId;
      const sourceLayer = sourceLayerId ? state.layers[sourceLayerId] : undefined;
      const targetLayerId = sourceLayer?.locked
        ? createAutoLayerForNewElements(state)
        : getValidTargetLayerId(state, sourceLayerId);

      const railType =
        typeof original.customProperties?.railType === 'string'
          ? original.customProperties.railType
          : undefined;
      const alloc = createEntityIdAllocator(Object.keys(state.elements));
      const duplicate: LayoutElement = {
        ...original,
        id: alloc.nextForElement(original.type, railType),
        layerId: targetLayerId,
        position: {
          ...original.position,
          x: original.position.x + 20,
          y: original.position.y + 20,
        },
        metadata: {
          ...original.metadata,
          name: getNextEntityName(state, original.type),
          layerId: targetLayerId,
        },
      };
      cloneElementPortsForDuplicate(original, duplicate);

      state.elements[duplicate.id] = duplicate;
      state.elementOrder.push(duplicate.id);
      state.elementOrder = withOhtsOnTop(state.elementOrder, state.elements);

      if (!state.layers[targetLayerId].elementIds.includes(duplicate.id)) {
        state.layers[targetLayerId].elementIds.push(duplicate.id);
      }
    },

    duplicateElements: (state, action: PayloadAction<string[]>) => {
      const newElements: LayoutElement[] = [];
      const lockedLayerCloneMap: Record<string, string> = {};
      const alloc = createEntityIdAllocator(Object.keys(state.elements));

      const resolveTargetLayerId = (sourceLayerId?: string) => {
        const candidate = getValidTargetLayerId(state, sourceLayerId);
        const sourceLayer = state.layers[candidate];
        if (!sourceLayer?.locked) return candidate;

        if (!lockedLayerCloneMap[candidate]) {
          lockedLayerCloneMap[candidate] = createAutoLayerForNewElements(state);
        }
        return lockedLayerCloneMap[candidate];
      };

      action.payload.forEach((id) => {
        const original = state.elements[id];
        if (!original) return;

        const sourceLayerId = original.layerId || original.metadata.layerId;
        const targetLayerId = resolveTargetLayerId(sourceLayerId);
        const railType =
          typeof original.customProperties?.railType === 'string'
            ? original.customProperties.railType
            : undefined;

        const duplicate: LayoutElement = {
          ...original,
          id: alloc.nextForElement(original.type, railType),
          layerId: targetLayerId,
          position: {
            ...original.position,
            x: original.position.x + 20,
            y: original.position.y + 20,
          },
          metadata: {
            ...original.metadata,
            name: getNextEntityName(state, original.type, newElements),
            layerId: targetLayerId,
          },
        };
        cloneElementPortsForDuplicate(original, duplicate);

        newElements.push(duplicate);
      });

      newElements.forEach((element) => {
        state.elements[element.id] = element;
        state.elementOrder.push(element.id);

        if (!state.layers[element.layerId].elementIds.includes(element.id)) {
          state.layers[element.layerId].elementIds.push(element.id);
        }
      });
      state.elementOrder = withOhtsOnTop(state.elementOrder, state.elements);
    },

    reorderElements: (state, action: PayloadAction<string[]>) => {
      state.elementOrder = withOhtsOnTop(action.payload, state.elements);
    },

    createLayer: (
      state,
      action: PayloadAction<
        { name?: string; visible?: boolean; locked?: boolean; setActive?: boolean } | undefined
      >
    ) => {
      const id = generateId();
      const name = action.payload?.name?.trim() || nextLayerName(state);
      const zIndex = state.layerOrder.length;
      const visible = action.payload?.visible ?? true;
      const locked = action.payload?.locked ?? false;
      const setActive = action.payload?.setActive ?? true;

      state.layers[id] = {
        id,
        name,
        visible,
        locked,
        zIndex,
        elementIds: [],
      };
      state.layerOrder.push(id);
      if (setActive) {
        state.activeLayerId = id;
      }
    },

    setActiveLayer: (state, action: PayloadAction<string>) => {
      if (state.layers[action.payload]) {
        state.activeLayerId = action.payload;
      }
    },

    toggleLayerLock: (state, action: PayloadAction<string>) => {
      const layer = state.layers[action.payload];
      if (!layer) return;
      layer.locked = !layer.locked;
    },

    reassignElementsToLayer: (
      state,
      action: PayloadAction<{ elementIds: string[]; layerId: string }>
    ) => {
      const { elementIds, layerId } = action.payload;
      if (!state.layers[layerId]) return;

      elementIds.forEach((elementId) => {
        const element = state.elements[elementId];
        if (!element) return;
        if (element.layerId === layerId) return;

        removeElementFromAllLayers(state, elementId);
        state.layers[layerId].elementIds.push(elementId);
        state.elements[elementId] = { ...element, layerId };
      });
    },

    deleteLayerIfEmpty: (state, action: PayloadAction<string>) => {
      const layerId = action.payload;
      if (layerId === DEFAULT_LAYER_ID) return;

      const layer = state.layers[layerId];
      if (!layer || layer.elementIds.length > 0) return;

      delete state.layers[layerId];
      state.layerOrder = state.layerOrder.filter((id) => id !== layerId);
      if (state.activeLayerId === layerId) {
        state.activeLayerId = DEFAULT_LAYER_ID;
      }
      ensureDefaultLayer(state);
    },

    bringToFront: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.elementOrder = withOhtsOnTop(
        [...state.elementOrder.filter((eId) => eId !== id), id],
        state.elements
      );
    },

    sendToBack: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      // Non-OHTs go to the very back; OHTs stay above all non-OHT elements.
      if (state.elements[id]?.type === 'oht-vehicle') {
        const others = state.elementOrder.filter((eId) => eId !== id);
        const nonOht = others.filter((eId) => state.elements[eId]?.type !== 'oht-vehicle');
        const oht = others.filter((eId) => state.elements[eId]?.type === 'oht-vehicle');
        state.elementOrder = [...nonOht, id, ...oht];
        return;
      }
      state.elementOrder = withOhtsOnTop(
        [id, ...state.elementOrder.filter((eId) => eId !== id)],
        state.elements
      );
    },

    clearLayout: (state) => {
      state.elements = {};
      state.elementOrder = [];
      state.layers = {
        [DEFAULT_LAYER_ID]: createDefaultLayer(),
      };
      state.layerOrder = [DEFAULT_LAYER_ID];
      state.activeLayerId = DEFAULT_LAYER_ID;
      state.portEditElementId = null;
      state.selectedPortId = null;
      state.selectedTrackNodeId = null;
      state.trackNodeAnnotations = [];
      state.projectMetadata = null;
      state.isDirty = false;
    },

    loadLayout: (
      state,
      action: PayloadAction<{
        elements: Record<string, LayoutElement>;
        order: string[];
        layers?: Record<string, Layer>;
        layerOrder?: string[];
        activeLayerId?: string;
        trackNodeAnnotations?: TrackNodeAnnotation[];
        projectMetadata?: Record<string, unknown> | null;
      }>
    ) => {
      state.elements = action.payload.elements;

      state.elementOrder = withOhtsOnTop(
        action.payload.order.filter((id) => !!state.elements[id]),
        state.elements
      );
      state.projectMetadata =
        action.payload.projectMetadata === undefined
          ? state.projectMetadata
          : action.payload.projectMetadata;

      const incomingLayers = action.payload.layers || {};
      const incomingLayerOrder = action.payload.layerOrder || Object.keys(incomingLayers);
      const normalizedLayers: Record<string, Layer> = {};

      incomingLayerOrder.forEach((layerId, index) => {
        const layer = incomingLayers[layerId];
        if (!layer) return;
        normalizedLayers[layerId] = {
          ...layer,
          id: layerId,
          zIndex: index,
          elementIds: [],
        };
      });

      if (!normalizedLayers[DEFAULT_LAYER_ID]) {
        normalizedLayers[DEFAULT_LAYER_ID] = createDefaultLayer();
      }

      state.elementOrder.forEach((elementId) => {
        const element = state.elements[elementId];
        if (!element) return;
        const elementLayerId = normalizedLayers[element.layerId]
          ? element.layerId
          : DEFAULT_LAYER_ID;
        if (element.layerId !== elementLayerId) {
          state.elements[elementId] = { ...element, layerId: elementLayerId };
        }
        normalizedLayers[elementLayerId].elementIds.push(elementId);
      });

      state.layers = normalizedLayers;
      state.layerOrder = [
        DEFAULT_LAYER_ID,
        ...incomingLayerOrder.filter(
          (layerId) => layerId !== DEFAULT_LAYER_ID && !!state.layers[layerId]
        ),
      ];

      const missingLayers = Object.keys(state.layers).filter(
        (layerId) => !state.layerOrder.includes(layerId)
      );
      state.layerOrder.push(...missingLayers);

      state.layerOrder.forEach((layerId, index) => {
        state.layers[layerId].zIndex = index;
      });

      state.activeLayerId = state.layers[action.payload.activeLayerId || '']
        ? (action.payload.activeLayerId as string)
        : DEFAULT_LAYER_ID;

      // Backfill a default port for stockers from projects saved before ports
      // existed, so every stocker always has at least one (no migration needed).
      Object.values(state.elements).forEach((element) => ensureElementHasPort(element));

      // Lane-snap used to set only a position, so vehicles saved before it also
      // set a rotation sit skewed across their rail. Re-seat the ones already
      // on a track; anything parked away from one is left alone.
      const elementList = Object.values(state.elements);
      for (const element of elementList) {
        if (element.type !== 'oht-vehicle') continue;
        const snap = findOHTLaneSnapPosition(element, elementList);
        if (!snap) continue;
        element.position = { ...element.position, x: snap.x, y: snap.y };
        element.rotation = snap.rotation;
      }

      state.portEditElementId = null;
      state.selectedPortId = null;
      state.selectedTrackNodeId = null;
      state.trackNodeAnnotations = Array.isArray(action.payload.trackNodeAnnotations)
        ? action.payload.trackNodeAnnotations.flatMap((annotation) => {
            if (
              annotation == null ||
              typeof annotation !== 'object' ||
              !Number.isFinite(annotation.x) ||
              !Number.isFinite(annotation.y) ||
              typeof annotation.name !== 'string' ||
              (annotation.role !== 'none' &&
                annotation.role !== 'entry' &&
                annotation.role !== 'exit' &&
                annotation.role !== 'junction')
            ) {
              return [];
            }
            const next: TrackNodeAnnotation = {
              x: annotation.x,
              y: annotation.y,
              name: annotation.name,
              role: annotation.role,
            };
            const capacity = Number(annotation.foupCapacity);
            if (Number.isFinite(capacity) && capacity > 0) {
              next.foupCapacity = capacity;
            }
            if (annotation.isJunction === true || annotation.role === 'junction') {
              next.isJunction = true;
            }
            return [next];
          })
        : [];
      ensureDefaultLayer(state);
      state.isDirty = false;
    },

    setIsDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },

    updateProjectMetadata: (state, action: PayloadAction<Record<string, unknown> | null>) => {
      state.projectMetadata = action.payload;
      state.isDirty = true;
    },

    patchProjectMetadata: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.projectMetadata = {
        ...(state.projectMetadata || {}),
        ...action.payload,
      };
      state.isDirty = true;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => {
        if (typeof action.type !== 'string') return false;
        const cleanActions = new Set([
          'layout/loadLayout',
          'layout/clearLayout',
          'layout/setIsDirty',
          'layout/setStockerPortEdit',
          'layout/selectLayoutPort',
          'layout/selectTrackNode',
          'layout/setActiveLayer',
          'layout/toggleLayerLock',
          'equipment/loadEquipment',
          'equipment/clearEquipment',
          'equipment/clearFuture',
          'equipment/clearHistory',
          'history/clearHistory',
          'history/clearFuture',
        ]);
        if (cleanActions.has(action.type)) return false;

        if (
          action.type.startsWith('equipment/setEquipmentReviewSelection') ||
          action.type.startsWith('equipment/toggleEquipmentReviewSelection') ||
          action.type.startsWith('equipment/setDraw') ||
          action.type.startsWith('equipment/popDrawPoint') ||
          action.type.startsWith('equipment/clearDrawPoints') ||
          action.type.startsWith('equipment/setPending') ||
          action.type.startsWith('equipment/clearPending') ||
          action.type.startsWith('equipment/beginEditInstance') ||
          action.type.startsWith('equipment/recordEquipmentHistory') ||
          action.type.startsWith('equipment/clearEquipmentRedo')
        ) {
          return false;
        }

        return (
          action.type.startsWith('layout/') ||
          action.type.startsWith('equipment/add') ||
          action.type.startsWith('equipment/update') ||
          action.type.startsWith('equipment/move') ||
          action.type.startsWith('equipment/translate') ||
          action.type.startsWith('equipment/remove') ||
          action.type.startsWith('equipment/duplicate') ||
          action.type.startsWith('equipment/setInstance') ||
          action.type.startsWith('equipment/undo') ||
          action.type.startsWith('equipment/redo') ||
          action.type === 'history/performUndo' ||
          action.type === 'history/performRedo'
        );
      },
      (state) => {
        state.isDirty = true;
      }
    );
  },
});

export const {
  addElement,
  addElements,
  removeElement,
  removeElements,
  updateElement,
  updateElements,
  transformElement,
  transformElements,
  setStockerPortEdit,
  selectLayoutPort,
  selectTrackNode,
  updateTrackNodeAnnotation,
  moveTrackNodeAnnotation,
  clearTrackNodeAnnotationAt,
  addStockerPort,
  updateStockerPort,
  removeStockerPort,
  duplicateElement,
  duplicateElements,
  reorderElements,
  createLayer,
  setActiveLayer,
  toggleLayerLock,
  reassignElementsToLayer,
  deleteLayerIfEmpty,
  bringToFront,
  sendToBack,
  clearLayout,
  loadLayout,
  setIsDirty,
  updateProjectMetadata,
  patchProjectMetadata,
} = layoutSlice.actions;

export default layoutSlice.reducer;
